import React from "react";
import { useSearchParams } from "react-router-dom";
import { Lock, RefreshCw, Mail, ExternalLink, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["new", "reviewed", "contacted"];
const STATUS_LABEL = {
  new: "New",
  reviewed: "Reviewed",
  contacted: "Contacted",
};
const STATUS_PALETTE = {
  new: {
    fg: "var(--md-green)",
    bg: "rgba(46,74,58,0.08)",
    ring: "var(--md-green)",
  },
  reviewed: {
    fg: "var(--md-gold)",
    bg: "rgba(181,138,75,0.12)",
    ring: "var(--md-gold)",
  },
  contacted: {
    fg: "var(--md-ink)",
    bg: "rgba(27,26,23,0.06)",
    ring: "var(--md-ink)",
  },
};

const StatusControl = ({ value, onChange, testidPrefix }) => {
  const safeValue = STATUS_OPTIONS.includes(value) ? value : "new";
  const palette = STATUS_PALETTE[safeValue];
  return (
    <label
      className="relative inline-flex items-center"
      data-testid={testidPrefix}
    >
      <span
        aria-hidden
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[0.7rem] tracking-[0.18em] uppercase"
        style={{
          color: palette.fg,
          background: palette.bg,
          border: `1px solid ${palette.ring}`,
        }}
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ background: palette.fg }}
        />
        {STATUS_LABEL[safeValue]}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M2 3.6l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <select
        value={safeValue}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Change lead status"
        className="absolute inset-0 opacity-0 cursor-pointer"
        data-testid={`${testidPrefix}-select`}
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABEL[s]}
          </option>
        ))}
      </select>
    </label>
  );
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

export default function AdminLeads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialToken = searchParams.get("token") || "";
  const [token, setToken] = React.useState(initialToken);
  const [tokenInput, setTokenInput] = React.useState(initialToken);
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [authed, setAuthed] = React.useState(false);

  const fetchLeads = React.useCallback(async (t) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/leads`, {
        headers: { "X-Admin-Token": t },
      });
      if (res.status === 401) {
        setAuthed(false);
        setError("Invalid admin token.");
        setLeads([]);
        return;
      }
      if (!res.ok) {
        throw new Error("Failed to load leads");
      }
      const data = await res.json();
      setLeads(data);
      setAuthed(true);
    } catch (e) {
      setError(e.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (initialToken) {
      fetchLeads(initialToken);
    }
  }, [initialToken, fetchLeads]);

  const handleAuth = (e) => {
    e.preventDefault();
    setToken(tokenInput);
    setSearchParams(tokenInput ? { token: tokenInput } : {});
    fetchLeads(tokenInput);
  };

  const handleDelete = async (leadId, leadName) => {
    const ok = window.confirm(
      `Delete the lead from ${leadName}? This cannot be undone.`,
    );
    if (!ok) return;
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": token },
      });
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Delete failed");
      }
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  const handleStatusChange = async (leadId, nextStatus) => {
    // Optimistic update
    const previousLeads = leads;
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, status: nextStatus } : l)),
    );
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": token,
        },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Status update failed");
      }
    } catch (e) {
      setLeads(previousLeads);
      setError(e.message || "Status update failed");
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--md-bg)" }}
      data-testid="admin-leads-page"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-[0.72rem] tracking-[0.22em] uppercase text-[color:var(--md-ink-mute)]">
              Maris Desk · Admin
            </p>
            <h1 className="font-display text-3xl sm:text-4xl text-[color:var(--md-ink)] mt-2">
              Guest Request Flow Review — Leads
            </h1>
          </div>
          {authed ? (
            <button
              type="button"
              onClick={() => fetchLeads(token)}
              className="md-btn-ghost text-sm"
              data-testid="refresh-button"
            >
              <RefreshCw size={16} /> Refresh
            </button>
          ) : null}
        </div>

        {!authed ? (
          <form
            onSubmit={handleAuth}
            className="md-card p-8 max-w-md"
            data-testid="admin-auth-form"
          >
            <div
              className="h-10 w-10 rounded-xl flex items-center justify-center mb-5"
              style={{
                background: "var(--md-green-soft)",
                color: "var(--md-green)",
              }}
            >
              <Lock size={18} />
            </div>
            <h2 className="font-display text-2xl text-[color:var(--md-ink)] leading-tight">
              Enter admin token
            </h2>
            <p className="mt-3 text-sm text-[color:var(--md-ink-soft)]">
              Provide the admin token to view submitted leads.
            </p>
            <label className="md-label mt-6" htmlFor="token">
              Admin token
            </label>
            <input
              id="token"
              type="password"
              className="md-input"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-token-input"
              required
            />
            {error ? (
              <p
                className="mt-4 text-sm"
                style={{ color: "var(--destructive)" }}
                data-testid="admin-auth-error"
              >
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              className="md-btn-primary mt-6"
              data-testid="admin-auth-submit"
            >
              View leads
            </button>
          </form>
        ) : (
          <div data-testid="admin-leads-content">
            <div className="md-card p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-[color:var(--md-ink-soft)]">
                <span className="font-medium text-[color:var(--md-ink)]">
                  {leads.length}
                </span>{" "}
                lead{leads.length === 1 ? "" : "s"} submitted
              </p>
              <p className="text-xs text-[color:var(--md-ink-mute)]">
                Sorted by most recent
              </p>
            </div>

            {loading ? (
              <p
                className="text-sm text-[color:var(--md-ink-mute)]"
                data-testid="admin-loading"
              >
                Loading…
              </p>
            ) : leads.length === 0 ? (
              <div className="md-card p-12 text-center" data-testid="admin-empty">
                <p className="font-display text-2xl text-[color:var(--md-ink)]">
                  No leads yet.
                </p>
                <p className="mt-2 text-sm text-[color:var(--md-ink-soft)]">
                  New submissions from the landing page will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-leads-list">
                {leads.map((lead) => (
                  <article
                    key={lead.id}
                    className="md-card p-6"
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <div className="flex items-start justify-between flex-wrap gap-4">
                      <div>
                        <h3 className="font-display text-xl text-[color:var(--md-ink)] leading-tight">
                          {lead.name}
                        </h3>
                        <p className="text-sm text-[color:var(--md-ink-soft)] mt-1">
                          {lead.property_name} · {lead.property_type}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusControl
                          value={lead.status || "new"}
                          onChange={(s) => handleStatusChange(lead.id, s)}
                          testidPrefix={`lead-status-${lead.id}`}
                        />
                        <span
                          className="text-[0.7rem] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full"
                          style={{
                            background: "var(--md-bg-sand)",
                            color: "var(--md-ink-soft)",
                            border: "1px solid var(--md-line)",
                          }}
                          data-testid={`lead-date-${lead.id}`}
                        >
                          {formatDate(lead.submitted_at)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDelete(lead.id, lead.name)}
                          className="inline-flex items-center justify-center h-9 w-9 rounded-full transition"
                          style={{
                            border: "1px solid var(--md-line)",
                            color: "var(--md-ink-mute)",
                            background: "transparent",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color =
                              "var(--destructive)";
                            e.currentTarget.style.borderColor =
                              "var(--destructive)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color =
                              "var(--md-ink-mute)";
                            e.currentTarget.style.borderColor =
                              "var(--md-line)";
                          }}
                          title="Delete lead"
                          aria-label={`Delete lead from ${lead.name}`}
                          data-testid={`lead-delete-${lead.id}`}
                        >
                          <Trash2 size={14} strokeWidth={1.7} />
                        </button>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
                      <div className="flex items-center gap-2 text-[color:var(--md-ink-soft)]">
                        <Mail size={14} />
                        <a
                          href={`mailto:${lead.email}`}
                          className="hover:text-[color:var(--md-green)] underline-offset-2 hover:underline"
                          data-testid={`lead-email-${lead.id}`}
                        >
                          {lead.email}
                        </a>
                      </div>
                      {lead.website ? (
                        <div className="flex items-center gap-2 text-[color:var(--md-ink-soft)]">
                          <ExternalLink size={14} />
                          <a
                            href={
                              lead.website.startsWith("http")
                                ? lead.website
                                : `https://${lead.website}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="hover:text-[color:var(--md-green)] underline-offset-2 hover:underline truncate"
                            data-testid={`lead-website-${lead.id}`}
                          >
                            {lead.website}
                          </a>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-5">
                      <p className="text-[0.7rem] uppercase tracking-[0.18em] text-[color:var(--md-ink-mute)] mb-1">
                        Main concern
                      </p>
                      <p
                        className="text-[color:var(--md-ink)]"
                        data-testid={`lead-concern-${lead.id}`}
                      >
                        {lead.main_concern}
                      </p>
                    </div>

                    {lead.intake_channels && lead.intake_channels.length > 0 ? (
                      <div className="mt-5">
                        <p className="text-[0.7rem] uppercase tracking-[0.18em] text-[color:var(--md-ink-mute)] mb-2">
                          Intake channels today
                        </p>
                        <div
                          className="flex flex-wrap gap-2"
                          data-testid={`lead-channels-${lead.id}`}
                        >
                          {lead.intake_channels.map((c) => (
                            <span
                              key={c}
                              className="text-[0.75rem] px-2.5 py-1 rounded-full"
                              style={{
                                background: "var(--md-bg-sand)",
                                color: "var(--md-ink-soft)",
                                border: "1px solid var(--md-line)",
                              }}
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {lead.message ? (
                      <div className="mt-5">
                        <p className="text-[0.7rem] uppercase tracking-[0.18em] text-[color:var(--md-ink-mute)] mb-1">
                          Notes
                        </p>
                        <p
                          className="text-[color:var(--md-ink-soft)] leading-relaxed whitespace-pre-line"
                          data-testid={`lead-message-${lead.id}`}
                        >
                          {lead.message}
                        </p>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
